import { useEffect, useRef, useCallback } from 'react';

interface NetworkBackgroundProps {
  dotsCount?: number;
  className?: string;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  baseX: number;
  baseY: number;
  color: string;
  pulsePhase: number;
  originalRadius: number;
  glowIntensity: number;
}

const NetworkBackground = ({ dotsCount = 100, className = "" }: NetworkBackgroundProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const mouseRef = useRef({ x: 0, y: 0, isActive: false });
  const animationFrameRef = useRef<number | undefined>(undefined);
  const lastFrameTimeRef = useRef<number>(0);
  const isVisibleRef = useRef<boolean>(true);

  // Mouse event handlers at component level
  const handleMouseMove = useCallback((e: MouseEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    mouseRef.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      isActive: true
    };
  }, []);

  const handleMouseLeave = useCallback(() => {
    mouseRef.current.isActive = false;
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      initParticles();
    };

    // Initialize particles
    const initParticles = () => {
      particlesRef.current = [];
      const colors = ['#9333EA', '#3B82F6', '#06B6D4', '#8B5CF6', '#6366F1'];
      for (let i = 0; i < dotsCount; i++) {
        const x = Math.random() * canvas.width;
        const y = Math.random() * canvas.height;
        const radius = Math.random() * 1.5 + 0.8;
        particlesRef.current.push({
          x,
          y,
          baseX: x,
          baseY: y,
          vx: (Math.random() - 0.5) * 3.5,
          vy: (Math.random() - 0.5) * 3.5,
          radius,
          originalRadius: radius,
          color: colors[Math.floor(Math.random() * colors.length)],
          pulsePhase: Math.random() * Math.PI * 2,
          glowIntensity: Math.random() * 0.6 + 0.6
        });
      }
    };

    // Animation loop with 20fps throttling
    const animate = (currentTime: number) => {
      // Skip if not visible
      if (!isVisibleRef.current) {
        animationFrameRef.current = requestAnimationFrame(animate);
        return;
      }

      // Throttle to ~20fps (50ms per frame)
      const elapsed = currentTime - lastFrameTimeRef.current;
      if (elapsed < 50) {
        animationFrameRef.current = requestAnimationFrame(animate);
        return;
      }
      lastFrameTimeRef.current = currentTime;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const particles = particlesRef.current;
      const mouse = mouseRef.current;
      const time = Date.now() * 0.001;

      // Update and draw particles
      particles.forEach((particle) => {
        // Pulsating effect
        particle.pulsePhase += 0.05;
        particle.radius = particle.originalRadius + Math.sin(particle.pulsePhase) * 0.5;

        // Move particles
        particle.x += particle.vx;
        particle.y += particle.vy;

        // Bounce off edges with randomness
        if (particle.x < 0 || particle.x > canvas.width) {
          particle.vx *= -1;
          particle.vx += (Math.random() - 0.5) * 0.3;
          particle.x = Math.max(0, Math.min(canvas.width, particle.x));
        }
        if (particle.y < 0 || particle.y > canvas.height) {
          particle.vy *= -1;
          particle.vy += (Math.random() - 0.5) * 0.3;
          particle.y = Math.max(0, Math.min(canvas.height, particle.y));
        }

        // Mouse interaction with stronger force
        if (mouse.isActive) {
          const dx = mouse.x - particle.x;
          const dy = mouse.y - particle.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const maxDistance = 120;

          if (distance < maxDistance) {
            const force = (maxDistance - distance) / maxDistance;
            particle.x -= (dx / distance) * force * 4;
            particle.y -= (dy / distance) * force * 4;
          }
        }
      });

      // Draw lines between nearby particles
      const maxDistance = 150;
      particles.forEach((p1, i) => {
        particles.slice(i + 1).forEach((p2) => {
          const dx = p2.x - p1.x;
          const dy = p2.y - p1.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < maxDistance) {
            const opacity = (1 - distance / maxDistance) * 0.6;
            const pulseEffect = Math.sin(time * 2 + distance * 0.02) * 0.25 + 0.85;
            
            ctx.beginPath();
            ctx.strokeStyle = `rgba(147, 51, 234, ${opacity * pulseEffect})`;
            ctx.lineWidth = 1.5;
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.stroke();
          }
        });

        // Draw connection to mouse if nearby
        if (mouse.isActive) {
          const dx = mouse.x - p1.x;
          const dy = mouse.y - p1.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const mouseMaxDistance = 220;

          if (distance < mouseMaxDistance) {
            const opacity = (1 - distance / mouseMaxDistance) * 0.75;
            ctx.beginPath();
            ctx.strokeStyle = `rgba(147, 51, 234, ${opacity})`;
            ctx.lineWidth = 2.5;
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(mouse.x, mouse.y);
            ctx.stroke();
          }
        }
      });

      // Draw particles
      particles.forEach((particle) => {
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
        ctx.fillStyle = particle.color;
        ctx.fill();
      });

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    // Intersection Observer to pause when not visible
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          isVisibleRef.current = entry.isIntersecting;
        });
      },
      { threshold: 0.1 }
    );

    observer.observe(canvas);

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseleave', handleMouseLeave);
    
    // Mark performance
    performance.mark('network-background-start');
    animate(0);
    performance.mark('network-background-ready');

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', resizeCanvas);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseleave', handleMouseLeave);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      
      // Measure performance
      try {
        performance.measure('network-background-init', 'network-background-start', 'network-background-ready');
      } catch (e) {
        // Ignore if marks don't exist
      }
    };
  }, [dotsCount]);

  return (
    <div className={`network-canvas ${className}`} style={{
      background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #4c1d95 100%)'
    }}>
      <canvas ref={canvasRef} className="w-full h-full" style={{ willChange: 'contents' }} />
    </div>
  );
};

export default NetworkBackground;
